import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { OWNER_HANDLE, OWNER_ID, OWNER_NAME, noteTemplates, seedFolders, seedNotes, shareCandidates } from '../data/notes';
import { account } from '../data/account';
import { challengeExpired, clearFailures, cooldownRemaining, createCredential, issueResetChallenge, registerFailure, verifyPin } from '../utils/notesLock';
import type { LockCredential, ResetChallenge } from '../utils/notesLock';
import { descendantFolderIds, newId } from '../utils/notes';
import type { Note, NoteAttachment, NoteBlock, NoteFolder, NoteReminder, NoteComment, NoteTemplateKey, Presence, SaveState, ShareRole } from '../types/notes';
interface NotesContextValue {
  notes: Note[];
  folders: NoteFolder[];
  saveState: SaveState;
  offline: boolean;
  /** Notes the owner created, in every lifecycle state. */
  ownNotes: Note[];
  /** Notes shared with the owner by someone else. */
  sharedWithMe: Note[];
  allTags: string[];
  roleFor: (note: Note) => ShareRole | null;
  createNote: (options?: {
    template?: NoteTemplateKey;
    folderId?: string | null;
    title?: string;
    diary?: boolean;
    academicLink?: Note['academicLinks'][number];
  }) => string;
  quickCapture: (text: string) => string;
  updateNote: (id: string, patch: Partial<Note>) => void;
  setBlocks: (id: string, blocks: NoteBlock[]) => void;
  setTitle: (id: string, title: string) => void;
  duplicateNote: (id: string) => string;
  moveNote: (id: string, folderId: string | null) => void;
  togglePin: (id: string) => void;
  archiveNote: (id: string) => void;
  restoreNote: (id: string) => void;
  trashNote: (id: string) => void;
  destroyNote: (id: string) => void;
  emptyTrash: () => void;
  addTag: (id: string, tag: string) => void;
  removeTag: (id: string, tag: string) => void;
  renameTag: (from: string, to: string) => void;
  addAttachment: (id: string, attachment: Omit<NoteAttachment, 'id'>) => void;
  removeAttachment: (id: string, attachmentId: string) => void;
  setReminder: (id: string, reminder: NoteReminder | undefined) => void;
  setLocked: (id: string, locked: boolean) => void;
  /* ---- Notes lock: one PIN for every locked note on the account ---- */
  lockCredential: LockCredential | null;
  /** Create the PIN for the first time. */
  createPin: (pin: string) => void;
  /** Replace it, proving knowledge of the current one. */
  changePin: (currentPin: string, nextPin: string) => boolean;
  /** Remove the PIN entirely, unlocking every locked note. */
  clearPin: (currentPin: string) => boolean;
  /** Verify and record success or failure against the throttle. */
  attemptUnlock: (pin: string) => boolean;
  /** Notes unlocked for this session. */
  unlockedNotes: string[];
  lockAllNotes: () => void;
  resetChallenge: ResetChallenge | null;
  sendResetCode: () => void;
  /** Verify the emailed code; returns false and counts the attempt when wrong. */
  verifyResetCode: (code: string) => boolean;
  resetChallengeVerified: boolean;
  cancelReset: () => void;
  /** After a verified code, set a brand new PIN. */
  resetPin: (pin: string) => void;
  share: (id: string, person: {
    displayName: string;
    handle: string;
  }, role: Exclude<ShareRole, 'owner'>) => void;
  setShareRole: (id: string, shareId: string, role: Exclude<ShareRole, 'owner'>) => void;
  revokeShare: (id: string, shareId: string) => void;
  setShareLink: (id: string, role: Exclude<ShareRole, 'owner'>, active: boolean) => void;
  createFolder: (name: string, parentId: string | null) => string;
  renameFolder: (id: string, name: string) => void;
  moveFolder: (id: string, parentId: string | null) => void;
  deleteFolder: (id: string) => void;
  setOffline: (value: boolean) => void;
  resolveConflict: (id: string, keep: 'mine' | 'theirs') => void;
  /** Snapshot the current content as a named version. */
  saveVersion: (id: string, summary: string) => void;
  /** Restore a snapshot by writing a NEW version — history is never rewound. */
  restoreVersion: (id: string, versionId: string) => void;
  addComment: (id: string, body: string, parentId?: string) => void;
  toggleCommentResolved: (id: string, commentId: string) => void;
  deleteComment: (id: string, commentId: string) => void;
  /** Collaborators currently in the given note. */
  presenceFor: (id: string) => Presence[];
  coEditing: boolean;
  setCoEditing: (value: boolean) => void;
}
const NotesContext = createContext<NotesContextValue | null>(null);
const now = '14 Aug 2026, 23:05';

/** @handle mentions resolve against people the note is actually shared with. */
function extractMentions(body: string) {
  const matches = body.match(/@([a-z0-9_.]+)/gi) ?? [];
  return matches.map(raw => raw.slice(1).toLowerCase()).map(handle => {
    if (handle === OWNER_HANDLE) return {
      handle: OWNER_HANDLE,
      displayName: OWNER_NAME
    };
    const person = shareCandidates.find(item => item.handle === handle);
    return person ? {
      handle: person.handle,
      displayName: person.displayName
    } : null;
  }).filter((item): item is {
    handle: string;
    displayName: string;
  } => item !== null);
}

/** Live collaborators, keyed by note. Presence is ephemeral, never stored. */
const PRESENCE_POOL = [{
  handle: 'ifeoma.e',
  displayName: 'Ifeoma Eze',
  colour: '#0f766e'
}, {
  handle: 'danielu',
  displayName: 'Daniel Uzo',
  colour: '#b45309'
}, {
  handle: 'tolua',
  displayName: 'Tolu Adeleke',
  colour: '#1d4ed8'
}];
export function NotesProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [notes, setNotes] = useState<Note[]>(() => seedNotes.map(note => ({
    ...note,
    comments: note.comments ?? []
  })));
  const [folders, setFolders] = useState<NoteFolder[]>(seedFolders);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [offline, setOfflineState] = useState(false);
  const [coEditing, setCoEditing] = useState(true);
  const [presence, setPresence] = useState<Record<string, Presence[]>>({});
  const notesRef = useRef<Note[]>([]);
  notesRef.current = notes;
  const [lockCredential, setLockCredential] = useState<LockCredential | null>(null);
  const [unlockedNotes, setUnlockedNotes] = useState<string[]>([]);
  const [resetChallenge, setResetChallenge] = useState<ResetChallenge | null>(null);
  const [resetChallengeVerified, setResetChallengeVerified] = useState(false);

  /**
   * Co-editing presence. Collaborators drift between blocks and start and stop
   * typing, mirroring what a realtime channel would push.
   */
  useEffect(() => {
    if (!coEditing) {
      setPresence({});
      return;
    }
    function tick() {
      const currentNotes = notesRef.current;
      const next: Record<string, Presence[]> = {};
      currentNotes.filter(note => note.shares.length > 0 && note.lifecycle === 'active').forEach(note => {
        const people = PRESENCE_POOL.filter(person => note.shares.some(share => share.handle === person.handle) || note.ownerId !== OWNER_ID);
        next[note.id] = people.filter(() => Math.random() > 0.35).map(person => ({
          ...person,
          blockId: note.blocks.length > 0 ? note.blocks[Math.floor(Math.random() * note.blocks.length)].id : null,
          typing: Math.random() > 0.5
        }));
      });
      setPresence(next);
    }
    tick();
    const interval = window.setInterval(tick, 2800);
    return () => window.clearInterval(interval);
  }, [coEditing]);

  /** Autosave: every mutation flashes Saving… then settles, or reports offline. */
  const touch = useCallback((id: string) => {
    if (offline) {
      setSaveState('offline');
    } else {
      setSaveState('saving');
      window.setTimeout(() => setSaveState('saved'), 420);
    }
    setNotes(current => current.map(note => note.id === id ? {
      ...note,
      updatedAt: now,
      syncState: offline ? 'offline' : note.syncState === 'conflict' ? 'conflict' : 'synced'
    } : note));
  }, [offline]);
  const mutate = useCallback((id: string, patch: (note: Note) => Note) => {
    setNotes(current => current.map(note => note.id === id ? patch(note) : note));
    touch(id);
  }, [touch]);
  const ownNotes = useMemo(() => notes.filter(note => note.ownerId === OWNER_ID), [notes]);
  const sharedWithMe = useMemo(() => notes.filter(note => note.ownerId !== OWNER_ID && note.lifecycle === 'active' && note.shares.some(entry => entry.handle === OWNER_HANDLE)), [notes]);
  const allTags = useMemo(() => {
    const set = new Set<string>();
    notes.forEach(note => note.tags.forEach(tag => set.add(tag)));
    return [...set].sort();
  }, [notes]);

  /** Authorization is by owner or share record — never by what the UI renders. */
  const roleFor = useCallback((note: Note): ShareRole | null => {
    if (note.ownerId === OWNER_ID) return 'owner';
    const entry = note.shares.find(item => item.handle === OWNER_HANDLE);
    return entry ? entry.role : null;
  }, []);
  const createNote = useCallback<NotesContextValue['createNote']>((options = {}) => {
    const template = noteTemplates.find(item => item.key === (options.template ?? 'blank'))!;
    const id = newId('n');
    const note: Note = {
      id,
      ownerId: OWNER_ID,
      ownerName: OWNER_NAME,
      title: options.title ?? (options.diary ? '14 August 2026' : template.defaultTitle),
      blocks: template.blocks.map(block => ({
        ...block,
        id: newId('b')
      })),
      contentFormat: 'campuzon-blocks-v1',
      folderId: options.folderId ?? null,
      tags: options.diary ? ['personal'] : [],
      attachments: [],
      academicLinks: options.academicLink ? [options.academicLink] : [],
      institution: 'University of Lagos',
      academicSession: '2026/2027',
      semester: 'First semester',
      createdAt: '14 Aug 2026',
      updatedAt: now,
      lastOpenedAt: 'just now',
      isPrivate: true,
      locked: false,
      shares: [],
      versions: [{
        id: newId('v'),
        savedOn: now,
        author: 'You',
        summary: 'Created'
      }],
      syncState: offline ? 'offline' : 'synced',
      lifecycle: 'active',
      pinned: false,
      diaryDate: options.diary ? '14 Aug 2026' : undefined,
      devices: ['Web · Chrome']
    };
    setNotes(current => [note, ...current]);
    setSaveState(offline ? 'offline' : 'saved');
    return id;
  }, [offline]);
  const quickCapture = useCallback<NotesContextValue['quickCapture']>(text => {
    const trimmed = text.trim();
    const firstLine = trimmed.split('\n')[0];
    const id = newId('n');
    const note: Note = {
      id,
      ownerId: OWNER_ID,
      ownerName: OWNER_NAME,
      title: firstLine.length > 48 ? `${firstLine.slice(0, 48)}…` : firstLine || 'Quick note',
      blocks: trimmed.split('\n').filter(Boolean).map(line => ({
        id: newId('b'),
        kind: 'paragraph' as const,
        html: line
      })),
      contentFormat: 'campuzon-blocks-v1',
      folderId: null,
      tags: [],
      attachments: [],
      academicLinks: [],
      createdAt: '14 Aug 2026',
      updatedAt: now,
      lastOpenedAt: 'just now',
      isPrivate: true,
      locked: false,
      shares: [],
      versions: [],
      syncState: offline ? 'offline' : 'synced',
      lifecycle: 'active',
      pinned: false,
      devices: ['Web · Chrome']
    };
    setNotes(current => [note, ...current]);
    setSaveState(offline ? 'offline' : 'saved');
    return id;
  }, [offline]);
  const value = useMemo<NotesContextValue>(() => ({
    notes,
    folders,
    saveState,
    offline,
    ownNotes,
    sharedWithMe,
    allTags,
    roleFor,
    createNote,
    quickCapture,
    updateNote: (id, patch) => mutate(id, note => ({
      ...note,
      ...patch
    })),
    setBlocks: (id, blocks) => mutate(id, note => ({
      ...note,
      blocks
    })),
    setTitle: (id, title) => mutate(id, note => ({
      ...note,
      title
    })),
    duplicateNote: id => {
      const source = notes.find(note => note.id === id);
      if (!source) return id;
      const copyId = newId('n');
      setNotes(current => [{
        ...source,
        id: copyId,
        title: `${source.title} (copy)`,
        blocks: source.blocks.map(block => ({
          ...block,
          id: newId('b')
        })),
        shares: [],
        shareLink: undefined,
        isPrivate: true,
        pinned: false,
        createdAt: '14 Aug 2026',
        updatedAt: now,
        versions: []
      }, ...current]);
      setSaveState('saved');
      return copyId;
    },
    moveNote: (id, folderId) => mutate(id, note => ({
      ...note,
      folderId
    })),
    togglePin: id => mutate(id, note => ({
      ...note,
      pinned: !note.pinned
    })),
    archiveNote: id => mutate(id, note => ({
      ...note,
      lifecycle: 'archived',
      pinned: false
    })),
    restoreNote: id => mutate(id, note => ({
      ...note,
      lifecycle: 'active',
      trashedOn: undefined
    })),
    trashNote: id => mutate(id, note => ({
      ...note,
      lifecycle: 'trashed',
      pinned: false,
      trashedOn: '14 Aug 2026'
    })),
    destroyNote: id => setNotes(current => current.filter(note => note.id !== id)),
    emptyTrash: () => setNotes(current => current.filter(note => note.lifecycle !== 'trashed')),
    addTag: (id, tag) => {
      const clean = tag.trim().replace(/^#/, '').toLowerCase();
      if (!clean) return;
      mutate(id, note => note.tags.includes(clean) ? note : {
        ...note,
        tags: [...note.tags, clean]
      });
    },
    removeTag: (id, tag) => mutate(id, note => ({
      ...note,
      tags: note.tags.filter(item => item !== tag)
    })),
    renameTag: (from, to) => {
      const clean = to.trim().replace(/^#/, '').toLowerCase();
      if (!clean) return;
      setNotes(current => current.map(note => ({
        ...note,
        tags: note.tags.map(tag => tag === from ? clean : tag)
      })));
    },
    addAttachment: (id, attachment) => mutate(id, note => ({
      ...note,
      attachments: [...note.attachments, {
        ...attachment,
        id: newId('a')
      }]
    })),
    removeAttachment: (id, attachmentId) => mutate(id, note => ({
      ...note,
      attachments: note.attachments.filter(item => item.id !== attachmentId)
    })),
    setReminder: (id, reminder) => mutate(id, note => ({
      ...note,
      reminder
    })),
    setLocked: (id, locked) => {
      mutate(id, note => ({
        ...note,
        locked
      }));
      setUnlockedNotes(current => locked ? [...new Set([...current, id])] : current.filter(item => item !== id));
    },
    lockCredential,
    createPin: pin => {
      setLockCredential(createCredential(pin, now));
      // Setting the PIN is itself proof of identity, so this session stays open.
      setUnlockedNotes(notesRef.current.filter(note => note.locked).map(note => note.id));
    },
    changePin: (currentPin, nextPin) => {
      if (!lockCredential || !verifyPin(lockCredential, currentPin)) {
        setLockCredential(current => current ? registerFailure(current) : current);
        return false;
      }
      setLockCredential(createCredential(nextPin, now));
      return true;
    },
    clearPin: currentPin => {
      if (!lockCredential || !verifyPin(lockCredential, currentPin)) {
        setLockCredential(current => current ? registerFailure(current) : current);
        return false;
      }
      setLockCredential(null);
      setNotes(current => current.map(note => note.locked ? {
        ...note,
        locked: false
      } : note));
      setUnlockedNotes([]);
      return true;
    },
    attemptUnlock: pin => {
      if (!lockCredential) return false;
      if (cooldownRemaining(lockCredential) > 0) return false;
      if (verifyPin(lockCredential, pin)) {
        setLockCredential(clearFailures(lockCredential));
        setUnlockedNotes(notesRef.current.filter(note => note.locked).map(note => note.id));
        return true;
      }
      setLockCredential(registerFailure(lockCredential));
      return false;
    },
    unlockedNotes,
    lockAllNotes: () => setUnlockedNotes([]),
    resetChallenge,
    sendResetCode: () => {
      setResetChallengeVerified(false);
      setResetChallenge(issueResetChallenge(account.email));
    },
    verifyResetCode: code => {
      if (!resetChallenge || challengeExpired(resetChallenge)) return false;
      if (resetChallenge.code === code.trim()) {
        setResetChallengeVerified(true);
        return true;
      }
      setResetChallenge({
        ...resetChallenge,
        attempts: resetChallenge.attempts + 1
      });
      return false;
    },
    resetChallengeVerified,
    cancelReset: () => {
      setResetChallenge(null);
      setResetChallengeVerified(false);
    },
    resetPin: pin => {
      setLockCredential(createCredential(pin, now));
      setResetChallenge(null);
      setResetChallengeVerified(false);
      setUnlockedNotes(notesRef.current.filter(note => note.locked).map(note => note.id));
    },
    share: (id, person, role) => mutate(id, note => note.shares.some(entry => entry.handle === person.handle) ? note : {
      ...note,
      isPrivate: false,
      shares: [...note.shares, {
        id: newId('s'),
        displayName: person.displayName,
        handle: person.handle,
        role,
        grantedOn: '14 Aug 2026'
      }]
    }),
    setShareRole: (id, shareId, role) => mutate(id, note => ({
      ...note,
      shares: note.shares.map(entry => entry.id === shareId ? {
        ...entry,
        role
      } : entry)
    })),
    revokeShare: (id, shareId) => mutate(id, note => {
      const shares = note.shares.filter(entry => entry.id !== shareId);
      return {
        ...note,
        shares,
        isPrivate: shares.length === 0 && !note.shareLink?.active
      };
    }),
    setShareLink: (id, role, active) => mutate(id, note => ({
      ...note,
      isPrivate: active ? false : note.shares.length > 0 ? false : true,
      shareLink: {
        id: note.shareLink?.id ?? newId('sl'),
        role,
        createdOn: '14 Aug 2026',
        active
      }
    })),
    createFolder: (name, parentId) => {
      const id = newId('f');
      setFolders(current => [...current, {
        id,
        name: name.trim() || 'New folder',
        parentId
      }]);
      return id;
    },
    renameFolder: (id, name) => setFolders(current => current.map(folder => folder.id === id ? {
      ...folder,
      name
    } : folder)),
    moveFolder: (id, parentId) => {
      if (id === parentId) return;
      if (parentId && descendantFolderIds(id, folders).includes(parentId)) return;
      setFolders(current => current.map(folder => folder.id === id ? {
        ...folder,
        parentId
      } : folder));
    },
    deleteFolder: id => {
      const doomed = [id, ...descendantFolderIds(id, folders)];
      setFolders(current => current.filter(folder => !doomed.includes(folder.id)));
      setNotes(current => current.map(note => note.folderId && doomed.includes(note.folderId) ? {
        ...note,
        folderId: null
      } : note));
    },
    setOffline: next => {
      setOfflineState(next);
      setSaveState(next ? 'offline' : 'saving');
      if (!next) window.setTimeout(() => setSaveState('saved'), 500);
    },
    saveVersion: (id, summary) => mutate(id, note => ({
      ...note,
      versions: [{
        id: newId('v'),
        savedOn: now,
        author: 'You',
        summary,
        blocks: note.blocks.map(block => ({
          ...block
        })),
        title: note.title
      }, ...(note.versions ?? [])]
    })),
    restoreVersion: (id, versionId) => mutate(id, note => {
      const version = note.versions.find(item => item.id === versionId);
      if (!version?.blocks) return note;
      return {
        ...note,
        title: version.title ?? note.title,
        blocks: version.blocks.map(block => ({
          ...block,
          id: newId('b')
        })),
        versions: [{
          id: newId('v'),
          savedOn: now,
          author: 'You',
          summary: `Restored the version from ${version.savedOn}`,
          blocks: note.blocks.map(block => ({
            ...block
          })),
          title: note.title
        }, ...note.versions]
      };
    }),
    addComment: (id, body, parentId) => mutate(id, note => {
      const mentions = extractMentions(body);
      if (parentId) {
        return {
          ...note,
          comments: (note.comments ?? []).map(comment => comment.id === parentId ? {
            ...comment,
            replies: [...comment.replies, {
              id: newId('c'),
              authorName: OWNER_NAME,
              authorHandle: OWNER_HANDLE,
              body,
              postedOn: now,
              mentions
            }]
          } : comment)
        };
      }
      const comment: NoteComment = {
        id: newId('c'),
        authorName: OWNER_NAME,
        authorHandle: OWNER_HANDLE,
        body,
        postedOn: now,
        mentions,
        resolved: false,
        replies: []
      };
      return {
        ...note,
        comments: [comment, ...(note.comments ?? [])]
      };
    }),
    toggleCommentResolved: (id, commentId) => mutate(id, note => ({
      ...note,
      comments: (note.comments ?? []).map(comment => comment.id === commentId ? {
        ...comment,
        resolved: !comment.resolved
      } : comment)
    })),
    deleteComment: (id, commentId) => mutate(id, note => ({
      ...note,
      comments: (note.comments ?? []).filter(comment => comment.id !== commentId)
    })),
    presenceFor: id => coEditing ? presence[id] ?? [] : [],
    coEditing,
    setCoEditing,
    resolveConflict: (id, keep) => mutate(id, note => ({
      ...note,
      syncState: 'synced',
      versions: [{
        id: newId('v'),
        savedOn: now,
        author: 'You',
        summary: keep === 'mine' ? 'Kept this device’s version' : 'Kept the server version'
      }, ...note.versions]
    }))
  }), [notes, folders, saveState, offline, ownNotes, sharedWithMe, allTags, roleFor, createNote, quickCapture, mutate, coEditing, presence, lockCredential, unlockedNotes, resetChallenge, resetChallengeVerified]);
  return <NotesContext.Provider value={value} data-id="element-6073" data-mp="3m0l8wif8d191">{children}</NotesContext.Provider>;
}
export function useNotes() {
  const context = useContext(NotesContext);
  if (!context) throw new Error('useNotes must be used inside NotesProvider');
  return context;
}